import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Minus, Plus, Trash2, ArrowRight } from 'lucide-react';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';
import { useSettings } from '@/hooks/useProducts';
import { formatCurrency } from '@/lib/utils';
import FreeShippingBar from '@/components/upsell/FreeShippingBar';
import Button from '@/components/ui/Button';

export default function CartDrawer() {
  const { t } = useTranslation('cart');
  const { items, updateQuantity, removeItem, subtotal, bundleDiscount, total, couponDiscount, couponCode } = useCartStore();
  const { isCartDrawerOpen, closeCartDrawer } = useUIStore();
  const { data } = useSettings();
  const navigate = useNavigate();
  const drawerRef = useRef<HTMLDivElement>(null);

  const config = data?.settings?.upsell_config?.cart_drawer;

  useEffect(() => {
    if (!isCartDrawerOpen) return;

    // Save the element that had focus before the drawer opened
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCartDrawer();

      // Focus trap: cycle Tab within the drawer
      if (e.key === 'Tab' && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    // Move focus into the drawer
    const timer = setTimeout(() => {
      const firstFocusable = drawerRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }, 50);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      // Restore focus to the element that opened the drawer
      previouslyFocused?.focus();
    };
  }, [isCartDrawerOpen, closeCartDrawer]);

  if (!config?.enabled) return null;

  return (
    <AnimatePresence>
      {isCartDrawerOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCartDrawer}
            aria-hidden="true"
          />

          {/* Drawer */}
          <motion.div
            ref={drawerRef}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-surface z-50 flex flex-col shadow-xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            role="dialog"
            aria-modal="true"
            aria-label={t('drawer.ariaLabel')}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-line">
              <h2 className="font-semibold text-ink flex items-center gap-2">
                <ShoppingBag className="h-5 w-5" aria-hidden="true" />
                {t('drawer.heading', { count: items.length })}
              </h2>
              <button
                onClick={closeCartDrawer}
                className="p-2 text-ink-muted hover:text-ink-soft rounded-lg hover:bg-surface-muted"
                aria-label={t('drawer.close')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Free shipping bar */}
            <div className="px-4 pt-3">
              <FreeShippingBar />
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="h-12 w-12 text-ink-muted mx-auto mb-3" aria-hidden="true" />
                  <p className="text-ink-muted">{t('drawer.empty')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => (
                    <div key={item.id} className="flex gap-3">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.product_name} className="w-16 h-16 object-cover rounded-md" />
                      ) : (
                        <div className="w-16 h-16 bg-surface-muted rounded-md flex items-center justify-center">
                          <ShoppingBag className="h-5 w-5 text-ink-muted" aria-hidden="true" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink truncate">{item.product_name}</p>
                        <p className="text-xs text-ink-muted">{item.variant_name}</p>
                        {item.bundle_name && (
                          <span className="text-[10px] bg-success-50 text-success-700 px-1 py-0.5 rounded font-medium inline-block mt-0.5">
                            {item.bundle_name}
                          </span>
                        )}
                        <div className="flex items-center justify-between mt-1">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => item.quantity > 1 ? updateQuantity(item.id, item.quantity - 1) : removeItem(item.id)}
                              className="p-1 text-ink-muted hover:text-ink-soft"
                              aria-label={t('drawer.decrementAriaLabel', { name: item.product_name })}
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="p-1 text-ink-muted hover:text-ink-soft"
                              disabled={item.quantity >= item.max_quantity}
                              aria-label={t('drawer.incrementAriaLabel', { name: item.product_name })}
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-sage-700">{formatCurrency(item.price * item.quantity)}</span>
                            <button onClick={() => removeItem(item.id)} className="text-red-400 hover:text-red-600" aria-label={t('item.removeAriaLabel', { name: item.product_name })}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-line px-4 py-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-ink-soft">{t('summary.subtotal')}</span>
                  <span className="font-semibold">{formatCurrency(subtotal())}</span>
                </div>
                {bundleDiscount() > 0 && (
                  <div className="flex justify-between text-sm text-success-700">
                    <span>{t('drawer.bundleSavings')}</span>
                    <span>-{formatCurrency(bundleDiscount())}</span>
                  </div>
                )}
                {couponCode && couponDiscount > 0 && (
                  <div className="flex justify-between text-sm text-success-700">
                    <span>{t('drawer.discountWithCode', { code: couponCode })}</span>
                    <span>-{formatCurrency(couponDiscount)}</span>
                  </div>
                )}
                <Button
                  className="w-full"
                  onClick={() => { closeCartDrawer(); navigate('/checkout'); }}
                >
                  {t('checkoutWithTotal', { total: formatCurrency(total()) })}
                  <ArrowRight className="h-4 w-4 ml-1" aria-hidden="true" />
                </Button>
                <Link
                  to="/cart"
                  onClick={closeCartDrawer}
                  className="block text-center text-sm text-sage-700 hover:text-sage-800"
                >
                  {t('viewCart')}
                </Link>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
